import React, { useState, useEffect } from "react";
import HeaderLinks from "components/Header/HeaderLinks.js";
import { useTranslation } from "react-i18next";
import { Mail, Map, Phone } from "@mui/icons-material";
import {
  Button,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  Container,
} from "@mui/material";
import useContactStyles from "../styles/contactUsStyle.js";
import { sendContactEmail } from "common/emailService.js";
import Header from "components/Header/Header.js";
import HomeFooter from "components/Footer/HomeFooter.js";

import contactUsBg from "../assets/img/contact-us-bg.webp";
import Availability from "components/Availability.js";

const dashboardRoutes = [];

export default function ContactUs(props) {
  const { ...rest } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <>
      <Header
        color="transparent"
        routes={dashboardRoutes}
        rightLinks={<HeaderLinks />}
        fixed
        changeColorOnScroll={{
          height: isMobile ? 200 : 400,
          color: "white",
        }}
        {...rest}
      />
      <Box
        sx={{
          position: "relative",
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          paddingTop: isMobile ? "160px" : "140px",
          paddingBottom: isMobile ? "40px" : "60px",
          backgroundImage: `url(${contactUsBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          isolation: "isolate",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.6) 100%)",
            zIndex: -1,
          },
        }}
      >
        <ContactSection isMobile={isMobile} isTablet={isTablet} />
      </Box>
      <Availability />
      <HomeFooter />
    </>
  );
}

export const ContactSection = ({ isMobile, isTablet }) => {
  const classes = useContactStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  // Always call hooks at the top level, not conditionally
  const mobileQuery = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletQuery = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Use props values if provided, otherwise use the query results
  const isMobileView = isMobile !== undefined ? isMobile : mobileQuery;
  const isTabletView = isTablet !== undefined ? isTablet : tabletQuery;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({
    type: "",
    message: "",
  });
  const [messageError, setMessageError] = useState("");

  // Responsive styling based on screen size
  const responsiveStyles = {
    container: {
      padding: isMobileView ? "16px" : isTabletView ? "24px" : "32px",
      maxWidth: isMobileView ? "100%" : "1200px",
      margin: "0 auto",
    },
    gridSpacing: isMobileView ? 3 : isTabletView ? 5 : 8,
    titleSize: isMobileView ? "1.75rem" : isTabletView ? "2.25rem" : "2.5rem",
    formTitleSize: isMobileView ? "1.35rem" : isTabletView ? "1.75rem" : "2rem",
    formPadding: isMobileView ? "16px" : isTabletView ? "24px" : "32px",
    buttonPadding: isMobileView ? "10px 15px" : "12px 24px",
    infoItemMargin: isMobileView ? "12px 0" : "20px 0",
    buttonMarginTop: isMobileView ? "16px" : "24px",
  };

  // Function to filter numeric characters
  const handleChangeWithFilter = (e) => {
    const { name, value } = e.target;

    // For name and message fields, filter out any numeric characters
    if (name === "name" || name === "message") {
      const filteredValue = value.replace(/[0-9]/g, "");

      // For message field, validate character count
      if (name === "message") {
        if (filteredValue.length > 160) {
          setMessageError("Message must be less than 160 characters");
        } else if (filteredValue.length < 10 && filteredValue.length > 0) {
          setMessageError("Message must be at least 10 characters");
        } else {
          setMessageError("");
        }
      }

      setFormData((prevState) => ({
        ...prevState,
        [name]: filteredValue,
      }));
    } else {
      // For other fields (like email), process normally
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  // Default handler for fields without special validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.message.length < 10 || formData.message.length > 160) {
      return;
    }

    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      const result = await sendContactEmail(formData);

      if (result && result.success) {
        setStatus({
          type: "success",
          message: "Message sent successfully! We'll get back to you shortly.",
        });

        // Reset form fields
        setFormData({
          name: "",
          email: "",
          message: "",
        });
        setMessageError("");
      } else {
        setStatus({
          type: "error",
          message: result.error || "Failed to send message. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setStatus({
        type: "error",
        message: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={responsiveStyles.container}>
      <Grid
        container
        spacing={responsiveStyles.gridSpacing}
        className={classes.gridContainer}
        alignItems="center"
        direction={isMobileView ? "column-reverse" : "row"}
      >
        <Grid item xs={12} sm={12} md={6}>
          <Typography
            variant={isMobileView ? "h3" : "h2"}
            sx={{
              fontSize: responsiveStyles.titleSize,
              fontWeight: 700,
              marginBottom: isMobileView ? "16px" : "24px",
              textAlign: isMobileView ? "center" : "left",
              color: "#fff",
              marginTop: isMobileView ? "0" : "48px",
            }}
          >
            {t(`getInTouch`)}
          </Typography>

          <Box
            className={classes.contactInfo}
            sx={{
              marginTop: isMobileView ? 2 : 4,
              marginBottom: isMobileView ? 3 : 0,
            }}
          >
            <Box
              className={classes.infoItem}
              sx={{
                margin: responsiveStyles.infoItemMargin,
                display: "flex",
                alignItems: "center",
                gap: "12px",
                color: "#fff",
              }}
            >
              <Map sx={{ fontSize: isMobileView ? "20px" : "24px" }} />
              <Typography
                variant={isMobileView ? "body2" : "body1"}
                sx={{ color: "#fff" }}
              >
                55 Sunset Ln, Barnstable, Massachusetts(MA), 02630
              </Typography>
            </Box>

            <Box
              className={classes.infoItem}
              sx={{
                margin: responsiveStyles.infoItemMargin,
                display: "flex",
                alignItems: "center",
                gap: "12px",
                color: "#fff",
              }}
            >
              <Phone
                sx={{
                  fontSize: isMobileView ? "20px" : "24px",
                  color: "#fff",
                }}
              />
              <Typography
                variant={isMobileView ? "body2" : "body1"}
                sx={{ color: "#fff" }}
              >
                (508) 362-8621
              </Typography>
            </Box>

            <Box
              className={classes.infoItem}
              sx={{
                margin: responsiveStyles.infoItemMargin,
                display: "flex",
                alignItems: "center",
                gap: "12px",
                color: "#fff",
              }}
            >
              <Mail sx={{ fontSize: isMobileView ? "20px" : "24px" }} />
              <Typography
                variant={isMobileView ? "body2" : "body1"}
                sx={{ color: "#fff" }}
              >
                info@harakaride.com
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <Box
            // className={classes.formContainer}
            sx={{
              padding: responsiveStyles.formPadding,
              borderRadius: "8px",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(4px)",
              marginBottom: isMobileView ? 4 : 0,
            }}
          >
            <Typography
              variant={isMobileView ? "h4" : "h3"}
              sx={{
                fontSize: responsiveStyles.formTitleSize,
                marginBottom: isMobileView ? "20px" : "28px",
                textAlign: isMobileView ? "center" : "left",
                color: "#fff",
              }}
            >
              {t(`sendMessage`)}
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                name="name"
                label={t(`name`)}
                variant="standard"
                className={classes.input}
                placeholder={t(`enterName`)}
                value={formData.name}
                onChange={handleChangeWithFilter}
                required
                margin={isMobileView ? "dense" : "normal"}
                InputProps={{
                  style: { color: "#fff" },
                  inputProps: {
                    pattern: "[A-Za-z\\s]+",
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                  style: { color: "#fff" },
                }}
              />

              <TextField
                fullWidth
                name="email"
                type={t(`email`)}
                label={t(`email`)}
                variant="standard"
                className={classes.input}
                placeholder={t(`email`)}
                value={formData.email}
                onChange={handleChange}
                required
                margin={isMobileView ? "dense" : "normal"}
                InputProps={{
                  style: { color: "#fff" },
                }}
                InputLabelProps={{
                  shrink: true,
                  style: { color: "#fff" },
                }}
              />

              <TextField
                fullWidth
                name="message"
                label={t(`message_text`)}
                variant="standard"
                multiline
                rows={isMobileView ? 3 : 4}
                className={classes.input}
                placeholder={t(`message_text`)}
                value={formData.message}
                onChange={handleChangeWithFilter}
                required
                margin={isMobileView ? "dense" : "normal"}
                error={!!messageError}
                helperText={
                  messageError ||
                  `Characters: ${formData.message.length}/160 (min: 10)`
                }
                FormHelperTextProps={{
                  style: {
                    color: messageError
                      ? "#f44336"
                      : "rgba(255, 255, 255, 0.7)",
                  },
                }}
                InputProps={{
                  style: { color: "#fff" },
                  inputProps: {
                    minLength: 10,
                    maxLength: 160,
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                  style: { color: "#fff" },
                }}
              />

              {status.message && (
                <Typography
                  sx={{
                    color: status.type === "success" ? "#4caf50" : "#f44336",
                    marginTop: "1rem",
                    textAlign: isMobileView ? "center" : "left",
                    fontSize: isMobileView ? "0.875rem" : "1rem",
                  }}
                >
                  {status.message}
                </Typography>
              )}

              <Button
                variant="contained"
                className={classes.submitButton}
                type="submit"
                fullWidth
                disabled={
                  loading || !!messageError || formData.message.length < 10
                }
                sx={{
                  marginTop: responsiveStyles.buttonMarginTop,
                  padding: responsiveStyles.buttonPadding,
                  fontSize: isMobileView ? "0.875rem" : "1rem",
                  fontWeight: "bold",
                  color: "#fff",
                  py: 1.5,
                  borderRadius: "50px",
                  textTransform: "none",
                  background:
                    "linear-gradient(90deg, #59A959 0%, #79B357 65.87%, #AFCD56 95.67%)",
                  "&:hover": { bgcolor: "#F8DBF2" },
                  "&:disabled": { background: "rgba(255, 255, 255, 0.5)" },
                  position: "relative",
                }}
              >
                {loading ? t(`sending`) : t(`sendMessage`)}
              </Button>
            </form>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};
